/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createOperationCallAndPoll } from 'src/core/services/api/createOperationCall';
import { httpService } from 'src/core/services/api/httpService';
import { ResponseResourcesValue } from 'src/core/types/genericApiResponse';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { QnaMakerSourcesSwaggers } from 'src/qnaMaker/core/services/sources/qnaMakerSourcesSwaggers';
import { QnaPatchOperation } from 'src/qnaMaker/core/types/qnaPatchOperation';
import { qnaMakerGenericFailureError } from 'src/qnaMaker/core/utils/qnaMakerOperation';

/**
 * Represents a service for manipulating QnA knowledge base sources.
 */
export const getQnaSourcesService = createService(({ context, getEnvironment }) => {
    const { apiUrl } = getEnvironment({ apiName: 'qna' });

    const getDefaultApiOptions = () =>
        luisRequestOptionsBuilder()
            .useDefaultOptions(context.serviceKey, true)
            .build();

    const getDefaultUrl = (id?: string) =>
        urlBuilder()
            .baseUrl(apiUrl)
            .project(id);

    const getSources = async ({ projectId }: { projectId: string }): Promise<QnaMakerSourcesSwaggers[]> => {
        const url = getDefaultUrl(projectId)
            .resource('sources')
            .queryParam('api-version', LuisConstants.QnaMaker.ApiVersion)
            .build();

        const { data: items } = await httpService.get<ResponseResourcesValue<QnaMakerSourcesSwaggers>>(url, getDefaultApiOptions());

        return items.value;
    };

    const updateSources = async ({
        projectId,
        sourcesWithOperation
    }: {
        projectId: string;
        sourcesWithOperation: QnaPatchOperation<Partial<QnaMakerSourcesSwaggers>>[];
    }): Promise<void> => {
        const url = getDefaultUrl(projectId)
            .resource('sources')
            .queryParam('api-version', LuisConstants.QnaMaker.ApiVersion)
            .build();

        // To be refactored when the backend returns the whole path in the location header
        const getJobStatusUrl = (jobId: string) =>
            getDefaultUrl(projectId)
                .resource('sources')
                .resource('jobs', jobId)
                .queryParam('api-version', LuisConstants.QnaMaker.ApiVersion)
                .build();

        const pathOp = httpService.patch<void>(url, sourcesWithOperation, getDefaultApiOptions());

        await createOperationCallAndPoll(
            pathOp,
            getDefaultApiOptions(),
            {
                timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                retryStrategy: res => res.data.status !== 'succeeded' && res.data.status !== 'failed',
                handleCall: res => {
                    if (res.status === 'failed') {
                        let err;
                        if (res.errors?.length) {
                            err = new Error(
                                res.errors[0].message.concat(`\n ${res.errors[0].details?.map(d => `- ${d.code}: ${d.message}\n`)}`)
                            );
                            err.name = res.errors[0].code;
                        } else {
                            err = qnaMakerGenericFailureError(res.jobId);
                        }

                        throw err;
                    }
                }
            },
            // ToDo: will be revisited once the backend returns the whole URI in the location header
            response =>
                getJobStatusUrl(
                    response.headers[LuisConstants.OperationHeaders.LocationHeader]
                        .split('/')
                        .pop()
                        .split('?')[0]
                ),
            LuisConstants.QnaMaker.LroPollMaxNumberOfTrials
        );
    };

    return {
        getSources,
        updateSources
    };
});
